import axios from "axios";

/**
 * Decodifica um JWT sem validar a assinatura (apenas para ler o payload)
 * @param {string} token - Token JWT
 * @returns {object|null} - Payload decodificado ou null se inválido
 */
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Erro ao decodificar token:", error);
    return null;
  }
};

/**
 * Verifica se o token está próximo de expirar
 * @param {string} token - Token JWT
 * @param {number} minutesBeforeExpiry - Minutos antes da expiração para considerar "próximo" (padrão: 15 min)
 * @returns {boolean} - true se o token está próximo de expirar ou já expirou
 */
export const isTokenExpiringSoon = (token, minutesBeforeExpiry = 15) => {
  if (!token) return true;

  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return true;

  const expirationTime = payload.exp * 1000; // Converter para milissegundos
  const now = Date.now();
  const timeUntilExpiry = expirationTime - now;
  const threshold = minutesBeforeExpiry * 60 * 1000; // Converter minutos para milissegundos

  return timeUntilExpiry <= threshold;
};

/**
 * Renova o token chamando o endpoint /login/refresh-token
 * @returns {Promise<boolean>} - true se renovação foi bem-sucedida
 */
export const refreshToken = async () => {
  try {
    const currentToken = localStorage.getItem("accessToken");

    if (!currentToken) {
      console.warn("Nenhum token encontrado para renovar");
      return false;
    }

    // Chama o endpoint de refresh com o token atual no header
    // IMPORTANTE: Usar axios diretamente para evitar loop infinito com o interceptor do Api
    const baseURL =
      process.env.REACT_APP_API_URL?.trim() || "http://localhost:3000";
    const response = await axios.post(
      `${baseURL}/login/refresh-token`,
      {},
      {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      },
    );

    if (response.data && response.data.accessToken) {
      // Atualiza o token no localStorage
      localStorage.setItem("accessToken", response.data.accessToken);

      // Atualiza também outros dados se fornecidos
      if (response.data.idUsuario) {
        localStorage.setItem("idUsuario", response.data.idUsuario);
      }
      if (response.data.perfil) {
        localStorage.setItem(
          "perfilUsuario",
          JSON.stringify(response.data.perfil),
        );
      }

      console.log("Token renovado com sucesso");
      return true;
    }

    return false;
  } catch (error) {
    console.error("Erro ao renovar token:", error);

    // Se o erro for 401 ou 403, significa que o token expirou ou usuário está inativo
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Limpar o localStorage e redirecionar para login
      const itemsToKeep = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("hideWelcomeDialog_v1_")) {
          itemsToKeep[key] = localStorage.getItem(key);
        }
      }

      localStorage.clear();

      Object.keys(itemsToKeep).forEach((key) => {
        localStorage.setItem(key, itemsToKeep[key]);
      });
      window.location.href = "/";
    }

    return false;
  }
};

/**
 * Verifica e renova o token automaticamente se necessário
 * @returns {Promise<boolean>} - true se o token está válido ou foi renovado com sucesso
 */
export const ensureValidToken = async () => {
  const token = localStorage.getItem("accessToken");

  if (!token) {
    return false;
  }

  if (isTokenExpiringSoon(token)) {
    return await refreshToken();
  }

  return true;
};

/**
 * Inicia verificação periódica do token (a cada 5 minutos)
 * Deve ser chamado após o login bem-sucedido
 */
export const startTokenRefreshInterval = () => {
  // Limpar intervalo anterior se existir
  if (window.tokenRefreshInterval) {
    clearInterval(window.tokenRefreshInterval);
  }

  // Verificar a cada 5 minutos
  window.tokenRefreshInterval = setInterval(
    async () => {
      const token = localStorage.getItem("accessToken");

      if (token && isTokenExpiringSoon(token, 30)) {
        // 30 minutos antes
        console.log("Token próximo de expirar, renovando...");
        await refreshToken();
      }
    },
    5 * 60 * 1000,
  ); // 5 minutos
};

/**
 * Para a verificação periódica do token
 * Deve ser chamado no logout
 */
export const stopTokenRefreshInterval = () => {
  if (window.tokenRefreshInterval) {
    clearInterval(window.tokenRefreshInterval);
    window.tokenRefreshInterval = null;
  }
};

export default {
  isTokenExpiringSoon,
  refreshToken,
  ensureValidToken,
  startTokenRefreshInterval,
  stopTokenRefreshInterval,
};
